import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign, Receipt, FileText, AlertTriangle, Building2, ChevronDown, ChevronUp, Calendar, BarChart3, PieChart, Clock, Link2, Unlink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinancialDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stripeData, setStripeData] = useState({});
  const [stripeStatus, setStripeStatus] = useState([]);
  const [xeroStatus, setXeroStatus] = useState([]);
  const [xeroData, setXeroData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [activeView, setActiveView] = useState('overview');

  useEffect(() => {
    fetchFinancials();
    // Check for Xero OAuth callback params
    const params = new URLSearchParams(window.location.search);
    if (params.get('xero_connected')) {
      window.history.replaceState({}, '', '/financial-dashboard');
    }
  }, []);

  const fetchFinancials = async () => {
    try {
      const [summaryRes, statusRes, xeroStatusRes] = await Promise.all([
        axios.get(`${API_URL}/api/financial/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/stripe/status`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/xero/status`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setData(summaryRes.data);
      setStripeStatus(statusRes.data);
      setXeroStatus(xeroStatusRes.data);

      // Fetch Stripe data for each connected company
      const connected = (statusRes.data || []).filter(s => s.connected);
      const stripeResults = {};
      for (const s of connected) {
        try {
          const res = await axios.get(`${API_URL}/api/stripe/${s.company_id}/data`, { withCredentials: true });
          if (res.data.connected) {
            stripeResults[s.company_id] = res.data;
          }
        } catch (err) {
          console.error(`Stripe fetch failed for ${s.company_name}:`, err);
        }
      }
      setStripeData(stripeResults);

      // Fetch Xero data for each connected company
      const xeroConnected = (xeroStatusRes.data || []).filter(s => s.connected);
      const xeroResults = {};
      for (const x of xeroConnected) {
        try {
          const res = await axios.get(`${API_URL}/api/xero/${x.company_id}/data`, { withCredentials: true });
          if (res.data.connected) {
            xeroResults[x.company_id] = res.data;
          }
        } catch (err) {
          console.error(`Xero fetch failed for ${x.company_name}:`, err);
        }
      }
      setXeroData(xeroResults);
    } catch (err) {
      console.error('Error fetching financial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const connectXero = (companyId) => {
    window.location.href = `${API_URL}/api/xero/authorize?company_id=${companyId}`;
  };

  const disconnectXero = async (companyId) => {
    try {
      await axios.delete(`${API_URL}/api/xero/${companyId}/disconnect`, { withCredentials: true });
      fetchFinancials();
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
  };

  const formatCurrency = (val) => {
    return `£${(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#D4AF37' }}></div>
          <p className="mt-4" style={{ color: '#8B7355' }}>Loading financials...</p>
        </div>
      </div>
    );
  }

  const totals = data?.totals || {};
  const byCompany = data?.by_company || [];
  const monthlyTrend = data?.monthly_trend || [];
  const hmrcDates = data?.hmrc_dates || [];

  // Aggregate Stripe data across all connected companies
  const stripeEntries = Object.values(stripeData);
  const stripeRevenue = stripeEntries.reduce((sum, s) => sum + (s.total_revenue || 0), 0);
  const stripePayouts = stripeEntries.reduce((sum, s) => sum + (s.total_payouts || 0), 0);
  const stripeBalanceAvail = stripeEntries.reduce((sum, s) => sum + (s.balance?.available || 0), 0);
  const stripeBalancePending = stripeEntries.reduce((sum, s) => sum + (s.balance?.pending || 0), 0);
  const stripeCustomers = stripeEntries.reduce((sum, s) => sum + (s.customer_count || 0), 0);
  const hasStripeData = stripeEntries.length > 0;

  // Aggregate Xero data
  const xeroEntries = Object.values(xeroData);
  const xeroIncome = xeroEntries.reduce((sum, x) => sum + (x.totals?.income || 0), 0);
  const xeroSpend = xeroEntries.reduce((sum, x) => sum + (x.totals?.spend || 0), 0);
  const xeroOutstanding = xeroEntries.reduce((sum, x) => sum + (x.totals?.outstanding || 0), 0);
  const hasXeroData = xeroEntries.length > 0;

  // Combined financials: prefer Xero income as true revenue if connected, fallback to Stripe
  const displayRevenue = hasXeroData ? xeroIncome : (hasStripeData ? stripeRevenue : totals.revenue);
  const displayExpenses = hasXeroData ? xeroSpend : totals.expenses;
  const displayProfit = displayRevenue - displayExpenses;
  const profitMargin = displayRevenue > 0 ? ((displayProfit / displayRevenue) * 100).toFixed(1) : '0.0';
  const dataSource = hasXeroData ? 'Xero' : (hasStripeData ? 'Stripe' : 'Manual');

  // Merge Stripe monthly trend
  const stripeMonthly = {};
  stripeEntries.forEach(s => {
    (s.monthly_trend || []).forEach(m => {
      stripeMonthly[m.month] = (stripeMonthly[m.month] || 0) + m.revenue;
    });
  });

  // Merge Xero monthly trend
  const combinedMonthly = {};
  xeroEntries.forEach(x => {
    (x.monthly_trend || []).forEach(m => {
      if (!combinedMonthly[m.month]) combinedMonthly[m.month] = { revenue: 0, expenses: 0 };
      combinedMonthly[m.month].revenue += m.income || 0;
      combinedMonthly[m.month].expenses += m.spend || 0;
    });
  });
  // Fallback to Stripe monthly if no Xero
  if (Object.keys(combinedMonthly).length === 0) {
    Object.entries(stripeMonthly).forEach(([month, rev]) => {
      combinedMonthly[month] = { revenue: rev, expenses: 0 };
    });
  }
  const allMonthlyTrend = Object.keys(combinedMonthly).length > 0
    ? Object.entries(combinedMonthly).sort(([a],[b]) => a.localeCompare(b)).map(([month, data]) => ({ month, revenue: data.revenue, expenses: data.expenses }))
    : monthlyTrend;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="financial-dashboard">
      {/* Header */}
      <header style={{ backgroundColor: '#2C2416' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-full transition-all hover:bg-white/10"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" style={{ color: '#D4AF37' }} />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-light text-white tracking-wide">Financial Dashboard</h1>
                <p className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>
                  At a Glance — All Companies
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {['overview', 'companies', 'hmrc'].map((view) => (
                <button
                  key={view}
                  onClick={() => setActiveView(view)}
                  className="px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all capitalize"
                  style={{
                    backgroundColor: activeView === view ? '#D4AF37' : 'transparent',
                    color: activeView === view ? '#2C2416' : '#8B7355',
                    border: activeView === view ? 'none' : '1px solid #3D3526',
                  }}
                  data-testid={`view-tab-${view}`}
                >
                  {view === 'hmrc' ? 'HMRC & Filings' : view}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* ============ OVERVIEW VIEW ============ */}
        {activeView === 'overview' && (
          <>
            {/* Top-Level KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg border p-5 sm:p-6" style={{ borderColor: '#E5E1DB' }} data-testid="kpi-revenue">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4" style={{ color: '#16A34A' }} />
                  <span className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>Revenue</span>
                  {hasStripeData && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>{dataSource}</span>}
                </div>
                <div className="text-2xl sm:text-3xl font-light" style={{ color: '#16A34A' }}>{formatCurrency(displayRevenue)}</div>
              </div>
              <div className="bg-white rounded-lg border p-5 sm:p-6" style={{ borderColor: '#E5E1DB' }} data-testid="kpi-expenses">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-4 h-4" style={{ color: '#DC2626' }} />
                  <span className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>Expenses</span>
                </div>
                <div className="text-2xl sm:text-3xl font-light" style={{ color: '#DC2626' }}>{formatCurrency(displayExpenses)}</div>
              </div>
              <div className="bg-white rounded-lg border p-5 sm:p-6" style={{ borderColor: displayProfit >= 0 ? '#BBF7D0' : '#FECACA' }} data-testid="kpi-profit">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4" style={{ color: displayProfit >= 0 ? '#16A34A' : '#DC2626' }} />
                  <span className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>Net Profit</span>
                </div>
                <div className="text-2xl sm:text-3xl font-light" style={{ color: displayProfit >= 0 ? '#16A34A' : '#DC2626' }}>
                  {formatCurrency(displayProfit)}
                </div>
                <div className="text-xs mt-1" style={{ color: '#8B7355' }}>{profitMargin}% margin</div>
              </div>
              <div className="bg-white rounded-lg border p-5 sm:p-6" style={{ borderColor: '#E5E1DB' }} data-testid="kpi-subscriptions">
                <div className="flex items-center gap-2 mb-2">
                  <Receipt className="w-4 h-4" style={{ color: '#D4AF37' }} />
                  <span className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>Monthly Subs</span>
                </div>
                <div className="text-2xl sm:text-3xl font-light" style={{ color: '#D4AF37' }}>{formatCurrency(totals.monthly_subscriptions)}</div>
                <div className="text-xs mt-1" style={{ color: '#8B7355' }}>{data?.subscription_count || 0} active</div>
              </div>
            </div>

            {/* Outstanding & Pending */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 p-6" data-testid="outstanding-invoices">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium" style={{ color: '#166534' }}>Outstanding Invoices</span>
                  <FileText className="w-4 h-4" style={{ color: '#16A34A' }} />
                </div>
                <div className="text-3xl font-light mb-1" style={{ color: '#166534' }}>
                  {formatCurrency(hasXeroData ? xeroOutstanding : totals.pending_invoices)}
                </div>
                <p className="text-xs" style={{ color: '#16A34A' }}>
                  {hasXeroData ? 'From Xero — money owed to you' : 'Money owed to you'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg border border-red-200 p-6" data-testid="pending-bills">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium" style={{ color: '#991B1B' }}>
                    {hasXeroData ? 'Total Spend' : 'Pending Bills'}
                  </span>
                  <Receipt className="w-4 h-4" style={{ color: '#DC2626' }} />
                </div>
                <div className="text-3xl font-light mb-1" style={{ color: '#991B1B' }}>
                  {formatCurrency(hasXeroData ? xeroSpend : totals.pending_bills)}
                </div>
                <p className="text-xs" style={{ color: '#DC2626' }}>
                  {hasXeroData ? 'From Xero — across all companies' : 'Money you owe'}
                </p>
              </div>
            </div>

            {/* Stripe Balance & Customers */}
            {hasStripeData && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DB' }} data-testid="stripe-balance">
                  <div className="text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>Stripe Balance</div>
                  <div className="text-2xl font-light" style={{ color: '#16A34A' }}>{formatCurrency(stripeBalanceAvail)}</div>
                  <div className="text-xs mt-1" style={{ color: '#8B7355' }}>
                    {formatCurrency(stripeBalancePending)} pending
                  </div>
                </div>
                <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DB' }} data-testid="stripe-payouts">
                  <div className="text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>Total Payouts</div>
                  <div className="text-2xl font-light" style={{ color: '#2C2416' }}>{formatCurrency(stripePayouts)}</div>
                  <div className="text-xs mt-1" style={{ color: '#8B7355' }}>To your bank</div>
                </div>
                <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DB' }} data-testid="stripe-customers">
                  <div className="text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>Customers</div>
                  <div className="text-2xl font-light" style={{ color: '#D4AF37' }}>{stripeCustomers}</div>
                  <div className="text-xs mt-1" style={{ color: '#8B7355' }}>Across connected accounts</div>
                </div>
              </div>
            )}

            {/* Recent Transactions from Stripe */}
            {hasStripeData && (
              <div className="bg-white rounded-lg border overflow-hidden mb-8" style={{ borderColor: '#E5E1DB' }} data-testid="stripe-transactions">
                <div className="px-5 py-3 flex items-center gap-2" style={{ backgroundColor: '#FDFCFB', borderBottom: '1px solid #E5E1DB' }}>
                  <Receipt className="w-4 h-4" style={{ color: '#D4AF37' }} />
                  <h3 className="text-sm font-medium" style={{ color: '#2C2416' }}>Recent Stripe Transactions</h3>
                  <span className="text-xs px-1.5 py-0.5 rounded-full ml-auto" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>Live Data</span>
                </div>
                <div className="divide-y" style={{ borderColor: '#F5F1EB' }}>
                  {Object.entries(stripeData).flatMap(([cid, sd]) =>
                    (sd.recent_charges || []).map(ch => ({ ...ch, company_name: sd.company_name }))
                  ).sort((a, b) => (b.created || 0) - (a.created || 0)).slice(0, 10).map((ch) => (
                    <div key={ch.id} className="flex items-center gap-3 px-5 py-3">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0`} style={{ backgroundColor: ch.paid ? '#16A34A' : '#F59E0B' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: '#2C2416' }}>{ch.description || 'Stripe charge'}</p>
                        <p className="text-xs" style={{ color: '#8B7355' }}>
                          {ch.company_name} — {ch.created ? new Date(ch.created * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-medium" style={{ color: '#16A34A' }}>
                          {formatCurrency(ch.amount)}
                        </p>
                        <p className="text-xs capitalize" style={{ color: ch.status === 'succeeded' ? '#16A34A' : '#F59E0B' }}>{ch.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Xero Transactions */}
            {hasXeroData && (
              <div className="bg-white rounded-lg border overflow-hidden mb-8" style={{ borderColor: '#E5E1DB' }} data-testid="xero-transactions">
                <div className="px-5 py-3 flex items-center gap-2" style={{ backgroundColor: '#FDFCFB', borderBottom: '1px solid #E5E1DB' }}>
                  <FileText className="w-4 h-4" style={{ color: '#0078C8' }} />
                  <h3 className="text-sm font-medium" style={{ color: '#2C2416' }}>Recent Xero Transactions</h3>
                  <span className="text-xs px-1.5 py-0.5 rounded-full ml-auto" style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}>Xero Live</span>
                </div>
                <div className="divide-y" style={{ borderColor: '#F5F1EB' }}>
                  {Object.entries(xeroData).flatMap(([cid, xd]) =>
                    (xd.recent_transactions || []).map(txn => ({ ...txn, company_name: xd.company_name }))
                  ).sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 12).map((txn, idx) => (
                    <div key={txn.id || idx} className="flex items-center gap-3 px-5 py-3">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                        backgroundColor: txn.type === 'RECEIVE' ? '#16A34A' : '#DC2626'
                      }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: '#2C2416' }}>{txn.contact || txn.reference || 'Bank transaction'}</p>
                        <p className="text-xs" style={{ color: '#8B7355' }}>
                          {txn.company_name} — {txn.date ? new Date(txn.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''} — {txn.account}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-medium" style={{ color: txn.type === 'RECEIVE' ? '#16A34A' : '#DC2626' }}>
                          {txn.type === 'RECEIVE' ? '+' : '-'}{formatCurrency(txn.total)}
                        </p>
                        <p className="text-xs capitalize" style={{ color: '#8B7355' }}>{txn.type?.toLowerCase()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Xero Invoices */}
            {hasXeroData && (
              <div className="bg-white rounded-lg border overflow-hidden mb-8" style={{ borderColor: '#E5E1DB' }} data-testid="xero-invoices">
                <div className="px-5 py-3 flex items-center gap-2" style={{ backgroundColor: '#FDFCFB', borderBottom: '1px solid #E5E1DB' }}>
                  <Receipt className="w-4 h-4" style={{ color: '#0078C8' }} />
                  <h3 className="text-sm font-medium" style={{ color: '#2C2416' }}>Invoices (Xero)</h3>
                  <span className="text-xs px-1.5 py-0.5 rounded-full ml-auto" style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}>
                    {xeroOutstanding > 0 ? `${formatCurrency(xeroOutstanding)} outstanding` : 'All paid'}
                  </span>
                </div>
                <div className="divide-y" style={{ borderColor: '#F5F1EB' }}>
                  {Object.entries(xeroData).flatMap(([cid, xd]) =>
                    (xd.recent_invoices || []).filter(inv => inv.type === 'ACCREC').map(inv => ({ ...inv, company_name: xd.company_name }))
                  ).sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 10).map((inv, idx) => (
                    <div key={inv.id || idx} className="flex items-center gap-3 px-5 py-3">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                        backgroundColor: inv.status === 'PAID' ? '#16A34A' : inv.amount_due > 0 ? '#F59E0B' : '#8B7355'
                      }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: '#2C2416' }}>
                          {inv.number ? `${inv.number} — ` : ''}{inv.contact}
                        </p>
                        <p className="text-xs" style={{ color: '#8B7355' }}>
                          {inv.company_name} — {inv.date ? new Date(inv.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-medium" style={{ color: '#2C2416' }}>{formatCurrency(inv.total)}</p>
                        {inv.amount_due > 0 ? (
                          <p className="text-xs" style={{ color: '#F59E0B' }}>{formatCurrency(inv.amount_due)} due</p>
                        ) : (
                          <p className="text-xs" style={{ color: '#16A34A' }}>Paid</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Monthly Trend Chart */}
            {allMonthlyTrend.length > 0 && (
              <div className="bg-white rounded-lg border p-6 mb-8" style={{ borderColor: '#E5E1DB' }} data-testid="monthly-trend">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  <h3 className="text-lg font-medium" style={{ color: '#2C2416' }}>Monthly Trend</h3>
                </div>
                <div className="flex items-end gap-2 h-40 overflow-x-auto">
                  {allMonthlyTrend.map((m) => {
                    const maxVal = Math.max(...allMonthlyTrend.map(x => Math.max(x.revenue, x.expenses || 0)), 1);
                    const revH = (m.revenue / maxVal) * 100;
                    const expH = (m.expenses / maxVal) * 100;
                    return (
                      <div key={m.month} className="flex flex-col items-center gap-1 min-w-[50px]">
                        <div className="flex items-end gap-0.5 h-28">
                          <div
                            className="w-4 rounded-t transition-all"
                            style={{ height: `${Math.max(revH, 2)}%`, backgroundColor: '#16A34A' }}
                            title={`Revenue: ${formatCurrency(m.revenue)}`}
                          />
                          <div
                            className="w-4 rounded-t transition-all"
                            style={{ height: `${Math.max(expH, 2)}%`, backgroundColor: '#DC2626' }}
                            title={`Expenses: ${formatCurrency(m.expenses)}`}
                          />
                        </div>
                        <span className="text-xs" style={{ color: '#8B7355' }}>{m.month.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#16A34A' }} />
                    <span className="text-xs" style={{ color: '#8B7355' }}>Revenue</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#DC2626' }} />
                    <span className="text-xs" style={{ color: '#8B7355' }}>Expenses</span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Company Breakdown */}
            <div className="bg-white rounded-lg border overflow-hidden mb-8" style={{ borderColor: '#E5E1DB' }} data-testid="company-breakdown-overview">
              <div className="px-5 py-3" style={{ backgroundColor: '#FDFCFB', borderBottom: '1px solid #E5E1DB' }}>
                <h3 className="text-sm font-medium" style={{ color: '#2C2416' }}>P&L by Company</h3>
              </div>
              <div className="divide-y" style={{ borderColor: '#F5F1EB' }}>
                {byCompany.filter(c => c.status !== 'dissolving').map((c) => {
                  const sd = stripeData[c.id];
                  const xd = xeroData[c.id];
                  const revenue = xd ? (xd.totals?.income || 0) : (sd ? sd.total_revenue : c.revenue);
                  const expenses = xd ? (xd.totals?.spend || 0) : c.expenses;
                  const net = revenue - expenses;
                  const isStripe = !!sd && !xd;
                  const isXero = !!xd;
                  return (
                    <div key={c.name} className="flex items-center gap-4 px-5 py-4">
                      <div className="w-3 h-10 rounded-sm" style={{ backgroundColor: c.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate" style={{ color: '#2C2416' }}>{c.name}</p>
                          {isXero && <span className="text-xs px-1 py-0.5 rounded" style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}>Xero</span>}
                          {isStripe && <span className="text-xs px-1 py-0.5 rounded" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>Stripe</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium" style={{ color: net >= 0 ? '#16A34A' : '#DC2626' }}>
                          {formatCurrency(net)}
                        </p>
                        <p className="text-xs" style={{ color: '#8B7355' }}>
                          In: {formatCurrency(revenue)} | Out: {formatCurrency(expenses)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* No Data Notice */}
            {!hasStripeData && totals.revenue === 0 && totals.expenses === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center" data-testid="no-data-notice">
                <BarChart3 className="w-10 h-10 mx-auto mb-3" style={{ color: '#D4AF37' }} />
                <h4 className="text-base font-medium mb-2" style={{ color: '#92400E' }}>Connect Your Accounts</h4>
                <p className="text-sm mb-4" style={{ color: '#8B7355' }}>
                  Link Stripe and Xero to auto-populate your financial data. In the meantime, you can add transactions manually via Payment Infrastructure.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => navigate('/infrastructure')}
                    className="px-4 py-2 rounded-sm text-sm font-medium transition-all"
                    style={{ backgroundColor: '#2C2416', color: '#D4AF37' }}
                    data-testid="go-to-infrastructure"
                  >
                    Payment Infrastructure
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ============ COMPANIES VIEW ============ */}
        {activeView === 'companies' && (
          <div className="space-y-4">
            {byCompany.map((c) => {
              const sd = stripeData[c.id];
              const xd = xeroData[c.id];
              const revenue = xd ? (xd.totals?.income || 0) : (sd ? sd.total_revenue : c.revenue);
              const expenses = xd ? (xd.totals?.spend || 0) : c.expenses;
              const net = revenue - expenses;
              const isExpanded = expandedCompany === c.name;
              const isStripeConnected = stripeStatus.some(s => s.company_id === c.id && s.connected);
              const isXeroConnected = xeroStatus.some(s => s.company_id === c.id && s.connected);
              return (
                <div key={c.name} className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DB' }} data-testid={`company-finance-${c.name}`}>
                  <button
                    onClick={() => setExpandedCompany(isExpanded ? null : c.name)}
                    className="w-full flex items-center gap-4 px-5 py-4 text-left transition-all hover:bg-gray-50"
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: c.color + '15' }}>
                      <Building2 className="w-5 h-5" style={{ color: c.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium" style={{ color: '#2C2416' }}>{c.name}</p>
                        {isXeroConnected && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}>Xero</span>}
                        {isStripeConnected && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>Stripe</span>}
                      </div>
                      <p className="text-xs" style={{ color: '#8B7355' }}>
                        {c.status === 'dissolving' ? 'Dissolving' : 'Active'}
                      </p>
                    </div>
                    <div className="text-right mr-2">
                      <p className="text-lg font-light" style={{ color: net >= 0 ? '#16A34A' : '#DC2626' }}>
                        {formatCurrency(net)}
                      </p>
                      <p className="text-xs" style={{ color: '#8B7355' }}>Net</p>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4" style={{ color: '#8B7355' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#8B7355' }} />}
                  </button>
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2" style={{ borderTop: '1px solid #F5F1EB' }}>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#166534' }}>
                            Income {xd ? '(Xero)' : sd ? '(Stripe)' : ''}
                          </p>
                          <p className="text-xl font-light" style={{ color: '#16A34A' }}>{formatCurrency(revenue)}</p>
                        </div>
                        <div className="p-3 rounded-lg" style={{ backgroundColor: '#FEF2F2' }}>
                          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#991B1B' }}>
                            Spend {xd ? '(Xero)' : ''}
                          </p>
                          <p className="text-xl font-light" style={{ color: '#DC2626' }}>{formatCurrency(expenses)}</p>
                        </div>
                        {xd && (
                          <>
                            <div className="p-3 rounded-lg" style={{ backgroundColor: '#FFFBEB' }}>
                              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#92400E' }}>Invoiced</p>
                              <p className="text-xl font-light" style={{ color: '#D4AF37' }}>{formatCurrency(xd.totals?.invoiced || 0)}</p>
                              {(xd.totals?.outstanding || 0) > 0 && (
                                <p className="text-xs mt-1" style={{ color: '#F59E0B' }}>{formatCurrency(xd.totals.outstanding)} outstanding</p>
                              )}
                            </div>
                            <div className="p-3 rounded-lg" style={{ backgroundColor: '#F0FEFF' }}>
                              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#0E7490' }}>Net P&L</p>
                              <p className="text-xl font-light" style={{ color: net >= 0 ? '#16A34A' : '#DC2626' }}>{formatCurrency(net)}</p>
                            </div>
                          </>
                        )}
                        {sd && !xd && (
                          <>
                            <div className="p-3 rounded-lg" style={{ backgroundColor: '#F0FEFF' }}>
                              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#0E7490' }}>Stripe Balance</p>
                              <p className="text-xl font-light" style={{ color: '#0891B2' }}>{formatCurrency(sd.balance?.available || 0)}</p>
                            </div>
                            <div className="p-3 rounded-lg" style={{ backgroundColor: '#FDF4FF' }}>
                              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#7C3AED' }}>Payouts</p>
                              <p className="text-xl font-light" style={{ color: '#8B5CF6' }}>{formatCurrency(sd.total_payouts || 0)}</p>
                            </div>
                          </>
                        )}
                        {!sd && !xd && (
                          <div className="col-span-2 p-2 rounded text-center text-xs" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                            No live data — connect Stripe or Xero
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ============ HMRC & FILINGS VIEW ============ */}
        {activeView === 'hmrc' && (
          <div>
            <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DB' }} data-testid="hmrc-dates">
              <div className="px-5 py-3 flex items-center gap-2" style={{ backgroundColor: '#FDFCFB', borderBottom: '1px solid #E5E1DB' }}>
                <Calendar className="w-4 h-4" style={{ color: '#D4AF37' }} />
                <h3 className="text-sm font-medium" style={{ color: '#2C2416' }}>HMRC Deadlines & Compliance Filings</h3>
              </div>
              {hmrcDates.length === 0 ? (
                <div className="px-5 py-8 text-center">
                  <Clock className="w-8 h-8 mx-auto mb-2" style={{ color: '#8B7355' }} />
                  <p className="text-sm" style={{ color: '#8B7355' }}>No HMRC filing deadlines recorded yet.</p>
                  <p className="text-xs mt-1" style={{ color: '#8B7355' }}>Deadlines will appear here as you add compliance items to your companies.</p>
                </div>
              ) : (
                <div className="divide-y" style={{ borderColor: '#F5F1EB' }}>
                  {hmrcDates.map((d) => {
                    const isPast = new Date(d.date) < new Date();
                    const isCompleted = d.status === 'completed';
                    return (
                      <div
                        key={d.id}
                        className="flex items-center gap-3 px-5 py-3"
                        style={{ backgroundColor: isPast && !isCompleted ? '#FEF2F2' : 'white' }}
                        data-testid={`hmrc-item-${d.id}`}
                      >
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: d.company_color }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: isPast && !isCompleted ? '#991B1B' : '#2C2416' }}>
                            {d.title}
                          </p>
                          <p className="text-xs" style={{ color: '#8B7355' }}>
                            {d.company_name} — {d.type}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs font-mono" style={{ color: isPast && !isCompleted ? '#DC2626' : '#2C2416' }}>
                            {new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                          {isCompleted && (
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>Done</span>
                          )}
                          {isPast && !isCompleted && (
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>Overdue</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Compliance Summary per Company */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {byCompany.filter(c => c.status !== 'dissolving').map((c) => {
                const companyDates = hmrcDates.filter(d => d.company_name === c.name);
                const pending = companyDates.filter(d => d.status !== 'completed');
                const overdue = companyDates.filter(d => d.status !== 'completed' && new Date(d.date) < new Date());
                return (
                  <div key={c.name} className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DB', borderTopWidth: '3px', borderTopColor: c.color }} data-testid={`hmrc-company-${c.name}`}>
                    <h4 className="text-sm font-medium mb-2" style={{ color: c.color }}>{c.name}</h4>
                    <div className="flex gap-4">
                      <div>
                        <p className="text-2xl font-light" style={{ color: '#2C2416' }}>{companyDates.length}</p>
                        <p className="text-xs" style={{ color: '#8B7355' }}>Total filings</p>
                      </div>
                      <div>
                        <p className="text-2xl font-light" style={{ color: pending.length > 0 ? '#D4AF37' : '#16A34A' }}>{pending.length}</p>
                        <p className="text-xs" style={{ color: '#8B7355' }}>Pending</p>
                      </div>
                      {overdue.length > 0 && (
                        <div>
                          <p className="text-2xl font-light" style={{ color: '#DC2626' }}>{overdue.length}</p>
                          <p className="text-xs" style={{ color: '#DC2626' }}>Overdue</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Data Sources */}
        <div className="mt-8 bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DB' }} data-testid="integration-status">
          <h4 className="text-sm font-medium mb-3" style={{ color: '#2C2416' }}>Data Sources</h4>
          
          {/* Stripe Connections */}
          <p className="text-xs uppercase tracking-widest mb-2 mt-2" style={{ color: '#8B7355' }}>Stripe</p>
          <div className="flex flex-wrap gap-3 mb-4">
            {stripeStatus.filter(s => s.slug || s.company_name === 'KiddiPlay').map(s => (
              <div key={`stripe-${s.company_id}`} className="flex items-center gap-2 px-3 py-2 rounded-full" style={{
                backgroundColor: s.connected ? '#DCFCE7' : s.company_name === 'KiddiPlay' ? '#F5F1EB' : '#FEF3C7',
                border: `1px solid ${s.connected ? '#86EFAC' : s.company_name === 'KiddiPlay' ? '#E5E1DB' : '#FCD34D'}`
              }}>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.connected ? '#16A34A' : s.company_name === 'KiddiPlay' ? '#8B7355' : '#F59E0B' }} />
                <span className="text-xs font-medium" style={{ color: s.connected ? '#166534' : s.company_name === 'KiddiPlay' ? '#8B7355' : '#92400E' }}>
                  {s.company_name} — {s.connected ? 'Connected' : s.company_name === 'KiddiPlay' ? 'Pending Launch' : 'Key Missing'}
                </span>
              </div>
            ))}
          </div>

          {/* Xero Connections */}
          <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>Xero Accounting</p>
          <div className="flex flex-wrap gap-3">
            {xeroStatus
              .filter(x => ['Spaza Global', 'Star Talent Group', 'KiddiPlay'].includes(x.company_name))
              .map(x => {
                const isPendingLaunch = x.company_name === 'KiddiPlay';
                return (
                  <div key={`xero-${x.company_id}`} className="flex items-center gap-2 px-3 py-2 rounded-full" style={{
                    backgroundColor: x.connected ? '#DCFCE7' : isPendingLaunch ? '#F5F1EB' : '#F5F1EB',
                    border: `1px solid ${x.connected ? '#86EFAC' : '#E5E1DB'}`
                  }}>
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: x.connected ? '#16A34A' : '#8B7355' }} />
                    <span className="text-xs font-medium" style={{ color: x.connected ? '#166534' : '#8B7355' }}>
                      {x.company_name} {x.connected ? `— ${x.tenant_name || 'Connected'}` : isPendingLaunch ? '— Pending Launch' : ''}
                    </span>
                    {!x.connected && !isPendingLaunch ? (
                      <button
                        onClick={() => connectXero(x.company_id)}
                        className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full transition-all hover:scale-105 ml-1"
                        style={{ backgroundColor: '#0078C8', color: 'white' }}
                        data-testid={`connect-xero-${x.company_id}`}
                      >
                        <Link2 className="w-3 h-3" /> Connect
                      </button>
                    ) : x.connected ? (
                      <button
                        onClick={() => disconnectXero(x.company_id)}
                        className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition-all hover:bg-red-100 ml-1"
                        style={{ color: '#DC2626' }}
                        data-testid={`disconnect-xero-${x.company_id}`}
                      >
                        <Unlink className="w-3 h-3" /> Disconnect
                      </button>
                    ) : null}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;
